// CDC growth-chart helpers shared by the staff growth tab and the family-portal growth screen.
// Percentiles are estimated from the bundled CDC reference curves (p3/p50/p97, BMI p5/p50/p85/p95).
import weightBoysData0_36 from '../constants/cdc/weight-for-age-boys-0-36.json';
import weightGirlsData0_36 from '../constants/cdc/weight-for-age-girls-0-36.json';
import weightBoysData2_20 from '../constants/cdc/weight-for-age-boys-2-20.json';
import weightGirlsData2_20 from '../constants/cdc/weight-for-age-girls-2-20.json';
import heightBoysData0_36 from '../constants/cdc/height-for-age-boys-0-36.json';
import heightGirlsData0_36 from '../constants/cdc/height-for-age-girls-0-36.json';
import heightBoysData2_20 from '../constants/cdc/height-for-age-boys-2-20.json';
import heightGirlsData2_20 from '../constants/cdc/height-for-age-girls-2-20.json';
import headCircBoysData from '../constants/cdc/head-circ-boys-0-36.json';
import headCircGirlsData from '../constants/cdc/head-circ-girls-0-36.json';
import bmiBoysData from '../constants/cdc/bmi-for-age-boys-2-20.json';
import bmiGirlsData from '../constants/cdc/bmi-for-age-girls-2-20.json';

export type GrowthMetric = 'weight' | 'height' | 'headCirc' | 'bmi';
export type GrowthRange = 'birth-36m' | '2-20y';

export interface CDCData {
  months: number[];
  p3: number[];
  p50: number[];
  p97: number[];
}

export interface BMICDCData {
  months: number[];
  p5: number[];
  p50: number[];
  p85: number[];
  p95: number[];
}

export function interpolate(xArr: number[], yArr: number[], x: number): number {
  if (x <= xArr[0]) return yArr[0];
  if (x >= xArr[xArr.length - 1]) return yArr[yArr.length - 1];
  let i = 0;
  while (i < xArr.length - 1 && xArr[i + 1] < x) i++;
  const t = (x - xArr[i]) / (xArr[i + 1] - xArr[i]);
  return yArr[i] + t * (yArr[i + 1] - yArr[i]);
}

export function getPercentileLabel(value: number, data: CDCData, ageMonths: number): string {
  const p3 = interpolate(data.months, data.p3, ageMonths);
  const p50 = interpolate(data.months, data.p50, ageMonths);
  const p97 = interpolate(data.months, data.p97, ageMonths);
  if (value <= p3) return '<3rd';
  if (value >= p97) return '>97th';
  if (value < p50) return Math.round(3 + ((value - p3) / (p50 - p3)) * 47) + 'th';
  return Math.round(50 + ((value - p50) / (p97 - p50)) * 47) + 'th';
}

export function getBMIPercentileLabel(bmi: number, data: BMICDCData, ageMonths: number): string {
  const p5 = interpolate(data.months, data.p5, ageMonths);
  const p50 = interpolate(data.months, data.p50, ageMonths);
  const p85 = interpolate(data.months, data.p85, ageMonths);
  const p95 = interpolate(data.months, data.p95, ageMonths);
  if (bmi < p5) return '<5th';
  if (bmi >= p95) return '≥95th';
  if (bmi >= p85) return Math.round(85 + ((bmi - p85) / (p95 - p85)) * 10) + 'th';
  if (bmi >= p50) return Math.round(50 + ((bmi - p50) / (p85 - p50)) * 35) + 'th';
  return Math.round(5 + ((bmi - p5) / (p50 - p5)) * 45) + 'th';
}

export function getBMICategory(
  bmi: number,
  data: BMICDCData,
  ageMonths: number
): { label: string; classes: string } {
  const p5 = interpolate(data.months, data.p5, ageMonths);
  const p85 = interpolate(data.months, data.p85, ageMonths);
  const p95 = interpolate(data.months, data.p95, ageMonths);
  if (bmi < p5)
    return { label: 'Underweight', classes: 'bg-red-50 text-red-700 border border-red-200' };
  if (bmi >= p95)
    return { label: 'Obese', classes: 'bg-red-50 text-red-700 border border-red-200' };
  if (bmi >= p85)
    return { label: 'Overweight', classes: 'bg-amber-50 text-amber-700 border border-amber-200' };
  return { label: 'Healthy', classes: 'bg-emerald-50 text-emerald-700 border border-emerald-200' };
}

const isMale = (sex: string) => sex.toLowerCase() === 'male' || sex.toLowerCase() === 'm';

export function getCDCData(metric: GrowthMetric, sex: string, range: GrowthRange): CDCData | null {
  const male = isMale(sex);
  if (metric === 'weight') {
    if (range === 'birth-36m')
      return male ? (weightBoysData0_36 as CDCData) : (weightGirlsData0_36 as CDCData);
    return male ? (weightBoysData2_20 as CDCData) : (weightGirlsData2_20 as CDCData);
  }
  if (metric === 'height') {
    if (range === 'birth-36m')
      return male ? (heightBoysData0_36 as CDCData) : (heightGirlsData0_36 as CDCData);
    return male ? (heightBoysData2_20 as CDCData) : (heightGirlsData2_20 as CDCData);
  }
  if (metric === 'headCirc') {
    return male ? (headCircBoysData as CDCData) : (headCircGirlsData as CDCData);
  }
  return null;
}

export function getBMICDCData(sex: string): BMICDCData {
  return isMale(sex) ? (bmiBoysData as BMICDCData) : (bmiGirlsData as BMICDCData);
}

const BIRTH_TO_36_MAX = 36;
const TWO_TO_20_MIN = 24;
const TWO_TO_20_MAX = 240;

/**
 * Percentile label for one measurement, or null when the CDC curves don't cover the age
 * (head circumference after 36 months, BMI before 24 months, anything after 20 years) or the
 * sex is unknown. Under 36 months uses the birth–36 month curves.
 */
export function percentileLabelFor(
  metric: GrowthMetric,
  value: number | undefined,
  sex: string | undefined,
  ageMonths: number
): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || !sex || !Number.isFinite(ageMonths) || ageMonths < 0) return null;
  const s = sex.toLowerCase();
  if (!['male', 'm', 'female', 'f'].includes(s)) return null;
  if (metric === 'bmi') {
    if (ageMonths < TWO_TO_20_MIN || ageMonths > TWO_TO_20_MAX) return null;
    return getBMIPercentileLabel(value, getBMICDCData(s), ageMonths);
  }
  if (metric === 'headCirc' && ageMonths > BIRTH_TO_36_MAX) return null;
  if (ageMonths > TWO_TO_20_MAX) return null;
  const data = getCDCData(metric, s, ageMonths <= BIRTH_TO_36_MAX ? 'birth-36m' : '2-20y');
  return data ? getPercentileLabel(value, data, ageMonths) : null;
}
